import React, { useRef } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { RootState } from '../../store';
import {
  hideSidePanel,
} from '../../store/actions/sidePanelActions';
import AddTaskComponent from '../Tasks/AddTaskComponent';
import { SidePanelState } from '../../store/reducer/sidePanelReducer';
import TimekeepingSidePanel from '../TimeTracker/SidePanel';
import Onboarding from '../Management/Employees/Onboarding';
import FileReaderComponent from './FileReader';
import TimeEntryDetailsSidePanel from '../TimeTracker/TimeEntryDetailsSidePanel';
import EmployeeCaseDetailPanel from '../Management/Employees/EmployeeCaseDetailPanel';
import EmployeeTaskDetailPanel from '../Management/Employees/EmployeeTaskDetailPanel';
import ExtractedContentSidePanel from './ExtractedContentSidePanel';
import TransactionDetails from './Subscriptions/TransactionDetails';
import CustomFileReader from './CustomFileReader';
import DraftTypeModal from './Drafts/DraftTypeModal';
import HelpContent from '../Help/Manual/HelpContent';
import DraftTypeFilesSidePanel from './Drafts/DraftTypeFilesSidePanel';
import ReaderComponent from './TailwindComponents/ReaderComponent';
import TaskReaderPane from '../Tasks/surface/TaskReaderPane';
import TableUploadReport from '../BatchTables/TableUploadReport';
import { streamUploadAndProcess } from '../../store/actions/BatchTables/BatchTablesActions';
import DraftSettingsSidePanel from './Drafts/DraftSettingsSidePanel';
import DocumentCustomizationPanel from './Drafts/DocumentCustomizationPanel';
import QuestionnairePanel from './QuestionnairePanel';
import CollaboratorsPanel from './CollaboratorPanel';
import LicenseDetailPanel from './Licenses/LicenseDetailPanel';
import AssignLicensePanel from '../Admin/AssignLicensePanel';
import ProjectResourcesPanel from '../Projects/ProjectResourcesPanel';
import ConnectToProjectPanel from '../Projects/ConnectToProjectPanel';
import ConnectToMatterPanel from '../Matters/surface/ConnectToMatterPanel';
import LinkTaskToMatterPanel from '../Tasks/LinkTaskToMatterPanel';
import MatterTimeEntryPanel from '../Matters/economics/MatterTimeEntryPanel';
import MatterPricingPanel from '../Matters/economics/MatterPricingPanel';
import ProjectPricingPanel from '../Projects/economics/ProjectPricingPanel';
import AIUsagePanel from './aiUsage/AIUsagePanel';
import { isAiUsageEnabled } from '../../config/featureFlags';
import type { AIUsageEntityType } from './aiUsage/aiUsageModel';
import AddClientToMatterModal from '../Matters/AddClientToMatterModal';
import ScanPanelContainer from '../Scan/ScanPanelContainer';
import ResourceCollaboratorsSidePanel from './ResourceCollaboratorsSidePanel';
import FileUploader from './TailwindComponents/FileUploader';
import AddSourceModalContent from './AddSourceModalContent';
import { PiXLight } from 'react-icons/pi';
import { useTranslation } from 'react-i18next';
import Button from '../shared/TailwindComponents/Button';
import ClientForm from '../Clients/ClientForm';
import ContactEditPanel from '../Clients/ContactEditPanel';
import AddTableDocumentPanel from '../BatchTables/AddTableDocumentPanel';
import SaveTableTemplatePanel from '../BatchTables/SaveTableTemplatePanel';
import BulkReviewPanel from '../BatchTables/BulkReviewPanel';
import ClauseRuleFormPanel from '../Review/ClauseRuleFormPanel';
import GlobalRuleFormPanel from '../Review/GlobalRuleFormPanel';
import DraftingCustomizePanel from '../Drafting/DraftingCustomizePanel';
import TemplatePreviewPanel from '../Drafting/wizard/TemplatePreviewPanel';
import TemplateMarkdownEditor from '../Templates/TemplateMarkdownEditor';
import WidgetGallery from '../Dashboard/WidgetGallery';

// Self-contained upload panel with file state management
const UploadAttachmentPanel: React.FC<{ onConfirm?: (data: any) => void; onClose: () => void }> = ({ onConfirm, onClose }) => {
  const [files, setFiles] = React.useState<File[]>([]);
  const { t } = useTranslation();
  return (
    <div className="flex flex-col h-full">
      <div className="flex-1 overflow-y-auto p-5">
        <FileUploader files={files} onFilesChange={setFiles} />
      </div>
      <div className="border-t border-gray-200 dark:border-gray-700 px-5 py-4 flex justify-end gap-2">
        <Button variant="neutral" size="small" onClick={onClose}>{t('Cancel')}</Button>
        <Button variant="primary" size="small" onClick={() => { if (onConfirm) { onConfirm({ files }); } onClose(); }} disabled={files.length === 0}>
          {t('Upload')}
        </Button>
      </div>
    </div>
  );
};

const GlobalSidePanel: React.FC = () => {
  const sidePanelState = useSelector(
    (state: RootState): SidePanelState => state.sidePanel
  );
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const contentRef = useRef<any>(null);

  const handleClose = () => {
    if (sidePanelState.onCancel) {
      sidePanelState.onCancel();
    }
    dispatch(hideSidePanel());
  };

  const renderContent = () => {
    switch (sidePanelState.type) {
      case 'addTask':
        return <AddTaskComponent />;
      case 'helpContent':
        return <HelpContent content={sidePanelState.content} />;
      case 'onboarding':
        return (
          <Onboarding
            onClose={handleClose}
          />
        );
      case 'addClient':
        return (
          <ClientForm
            onCancel={handleClose}
            hideHeader={true}
            onClientCreated={(client) => {
              if (sidePanelState.onClientCreated) {
                sidePanelState.onClientCreated(client);
              }
              handleClose();
            }}
          />
        )
      case 'editContact':
        return (
          <ContactEditPanel
            client={sidePanelState.client}
            contactTypes={sidePanelState.contactTypes || []}
            onSaved={sidePanelState.onSaved}
            onClose={handleClose}
          />
        );
      case 'timekeepingView':
        return (
          <TimekeepingSidePanel
            day={sidePanelState.day}
            data={sidePanelState.data}
            isLoading={sidePanelState.isLoading}
            stats={sidePanelState.stats}
            onClose={handleClose}
          />
        );
      case 'createProject':
        return <></>;


      case 'fileReader':
        return (
          <FileReaderComponent
            fileUrl={sidePanelState.fileUrl}
            fileType={sidePanelState.fileType}
          />
        )
      /*
        A TASK ATTACHMENT, in the drawer.

        Same body the task page rendered in its own pane - the Document info
        block over `ReaderComponent` - hosted here instead, so reading an
        attachment is the app's slide-in and not a container inside the page.
        Opening a second attachment dispatches over this state, so the drawer
        swaps its document rather than stacking a second one.
      */
      /* What a table's bulk upload actually did - uploaded / skipped / failed,
         by name, with one button to retry just the transport failures. */
      case 'tableUploadReport':
        return (
          <TableUploadReport
            data={sidePanelState.data}
            onRetry={(retryFiles) => {
              handleClose();
              dispatch(streamUploadAndProcess(sidePanelState.data?.tableUuid, retryFiles) as any);
            }}
            onClose={handleClose}
          />
        );
      case 'taskAttachmentReader':
        return (
          <TaskReaderPane
            content={sidePanelState.data}
            onClose={handleClose}
          />
        );
      case 'reader':
        return (
          <ReaderComponent
            citation={sidePanelState.citation}
            matterId={sidePanelState.matterId}
            sourceType={sidePanelState.sourceType as 'file' | 'law' | 'case' | 'document'}
            userAccessLevel={sidePanelState.userAccessLevel}
            onDelete={sidePanelState.onDelete}
          />
        );
      case 'fileReaderTst':
        return (
          <CustomFileReader
            fileUrl={sidePanelState.fileUrl}
            content={sidePanelState.content}
            isPdf={sidePanelState.isPdf}
            filename={sidePanelState.filename || 'Document'}
          />
        );
      case 'timeEntryDetails':
        return (
          <TimeEntryDetailsSidePanel
            entryData={sidePanelState.data}
            onClose={handleClose}
          />
        );

      case 'employeeTaskDetail':
        return <EmployeeTaskDetailPanel taskData={sidePanelState.taskData} />;

      case 'employeeCaseDetail':
        return <EmployeeCaseDetailPanel caseData={sidePanelState.caseData} />;
      case 'extractedContent':
        return (
          <ExtractedContentSidePanel
            filename={sidePanelState.filename}
            content={sidePanelState.content}
          />
        );
      case 'transactionDetails':
        return (
          <TransactionDetails transactionId={sidePanelState.transaction_id} />

        );
      case 'draftTypes':
        return (
          <DraftTypeModal
            caseId={sidePanelState.caseId}
            onClose={handleClose}
            onSave={sidePanelState.onSave!}
          />
        );
      case 'draftTypeFiles':
        return <DraftTypeFilesSidePanel ref={contentRef} caseId={sidePanelState.caseId} draftTypeName={sidePanelState.draftTypeName} />;
      case 'draftSettings':
        return <DraftSettingsSidePanel matterId={sidePanelState.matterId} draftId={sidePanelState.draftId} onClose={handleClose} />;
      case 'questionnairePanel':
        return (
          <QuestionnairePanel
            ref={contentRef}
            matterId={sidePanelState.matterId}
            questionIndex={sidePanelState.questionIndex}
            item={sidePanelState.item as any}
            onClose={handleClose}
            onUpdate={sidePanelState.onUpdate}
          />
        );
      case 'collaboratorsPanel':
        return (
          <CollaboratorsPanel
            ref={contentRef}
            caseId={sidePanelState.caseId}
            summary={sidePanelState.summary}
            onClose={handleClose}
            onUpdate={sidePanelState.onUpdate}
          />
        );
      case 'templateMarkdownEdit':
        return (
          <TemplateMarkdownEditor
            templateId={sidePanelState.templateId}
            templateName={sidePanelState.templateName}
            onClose={handleClose}
          />
        );
      case 'templatePreview':
        return (
          <TemplatePreviewPanel
            outline={sidePanelState.outline}
            description={sidePanelState.description}
          />
        );
      case 'documentCustomization':
        return (
          <DocumentCustomizationPanel
            matterUuid={sidePanelState.matterUuid}
            documentTypeId={sidePanelState.documentTypeId}
            documentType={sidePanelState.documentType}
            documentName={sidePanelState.documentName}
            onClose={handleClose}
          />
        );
      case 'licenseDetail':
        return (
          <LicenseDetailPanel
            plan={sidePanelState.plan}
            billingPeriod={sidePanelState.billingPeriod}
            isCurrentPlan={sidePanelState.isCurrentPlan}
            isActive={sidePanelState.isActive}
            currentPlanSortOrder={sidePanelState.currentPlanSortOrder}
            seatCount={sidePanelState.seatCount}
            seatsUsed={sidePanelState.seatsUsed}
            onUpdateSeats={sidePanelState.onUpdateSeats}
            onAction={(p) => {
              if (sidePanelState.onAction) sidePanelState.onAction(p);
              handleClose();
            }}
          />
        );
      case 'assignLicense':
        return (
          <AssignLicensePanel
            userIds={sidePanelState.userIds}
            userNames={sidePanelState.userNames}
            onClose={handleClose}
            onComplete={sidePanelState.onComplete}
          />
        );
      case 'connectToProject':
        return (
          <ConnectToProjectPanel
            resourceType={sidePanelState.resourceType}
            resourceUuid={sidePanelState.resourceUuid}
            onChanged={sidePanelState.onChanged}
            onClose={handleClose}
          />
        );
      case 'connectToMatter':
        return (
          <ConnectToMatterPanel
            matterUuid={sidePanelState.matterUuid}
            initialType={sidePanelState.initialType as any}
            onChanged={sidePanelState.onChanged}
            onClose={handleClose}
          />
        );
      case 'linkTaskToMatter':
        return (
          <LinkTaskToMatterPanel
            taskUuid={sidePanelState.taskUuid}
            currentMatter={sidePanelState.currentMatter}
            onChanged={sidePanelState.onChanged}
            onClose={handleClose}
          />
        );
      case 'projectResources':
        return (
          <ProjectResourcesPanel
            projectUuid={sidePanelState.projectUuid}
            onClose={handleClose}
            onResourceLinked={sidePanelState.onResourceLinked}
          />
        );
      case 'bulkReviewRepaper':
        return (
          <BulkReviewPanel
            table={(sidePanelState as any).table}
            preselectedRowIds={(sidePanelState as any).preselectedRowIds}
            onClose={handleClose}
          />
        );
      case 'addClientToMatter':
        return (
          <AddClientToMatterModal
            matterUuid={sidePanelState.matterUuid || ''}
            clientRoleOptions={sidePanelState.clientRoleOptions || []}
            existingMatterClients={(sidePanelState as any).existingMatterClients || []}
            onClientAdded={async () => {
              if (sidePanelState.onRefresh) await sidePanelState.onRefresh();
              if (sidePanelState.onAfterSave) await sidePanelState.onAfterSave();
            }}
          />
        );
      case 'uploadAttachment':
        return (
          <UploadAttachmentPanel
            onConfirm={sidePanelState.onConfirm}
            onClose={handleClose}
          />
        );
      case 'addSource':
        return (
          <AddSourceModalContent
            sourceType={(sidePanelState as any).sourceType || 'law'}
            onConfirm={(sourceData: any) => {
              if (sidePanelState.onConfirm) sidePanelState.onConfirm(sourceData);
              handleClose();
            }}
            onCancel={handleClose}
          />
        );
      case 'addTableDocument':
        return (
          <AddTableDocumentPanel
            tableUuid={sidePanelState.tableUuid}
            onClose={handleClose}
          />
        );
      case 'saveTableTemplate':
        return (
          <SaveTableTemplatePanel
            tableUuid={sidePanelState.tableUuid}
            defaultName={sidePanelState.defaultName}
            onClose={handleClose}
          />
        );
      case 'manageResourceCollaborators':
        return (
          <ResourceCollaboratorsSidePanel
            resourceUuid={sidePanelState.resourceUuid}
            resourceType={sidePanelState.resourceType}
            collaborators={sidePanelState.collaborators}
            currentUserUuid={sidePanelState.currentUserUuid}
            onUpdate={sidePanelState.onUpdate}
            canAdmin={sidePanelState.canAdmin}
            addCollaborator={sidePanelState.addCollaborator}
            removeCollaborator={sidePanelState.removeCollaborator}
            updateAccess={sidePanelState.updateAccess}
            title={sidePanelState.title}
            subMessage={sidePanelState.subMessage}
            icon={sidePanelState.icon}
            supportsLead={sidePanelState.supportsLead}
            isLead={sidePanelState.isLead}
            setLead={sidePanelState.setLead}
            ownerLabel={sidePanelState.ownerLabel}
          />
        );
      case 'editClauseRule':
        return (
          <ClauseRuleFormPanel
            ref={contentRef}
            reviewUuid={sidePanelState.reviewUuid}
            rule={sidePanelState.rule}
            onClose={handleClose}
          />
        );
      case 'editGlobalRule':
        return (
          <GlobalRuleFormPanel
            ref={contentRef}
            reviewUuid={sidePanelState.reviewUuid}
            rule={sidePanelState.rule}
            onClose={handleClose}
          />
        );
      case 'matterTimeEntry':
        return (
          <MatterTimeEntryPanel
            entry={sidePanelState.entry}
            matterUuid={sidePanelState.matterUuid}
            activityTypes={sidePanelState.activityTypes}
            fallbackLabel={sidePanelState.fallbackLabel}
            canManage={sidePanelState.canManage}
            allowDelete={sidePanelState.allowDelete}
            onSaved={sidePanelState.onSaved}
            onDelete={sidePanelState.onDeleteEntry}
            onClose={handleClose}
          />
        );
      case 'matterPricing':
        return (
          <MatterPricingPanel
            matterData={sidePanelState.matterData}
            currency={sidePanelState.currency}
            budgetAmount={sidePanelState.budgetAmount}
            onSaved={sidePanelState.onSaved}
            onClose={handleClose}
          />
        );
      case 'projectPricing':
        return (
          <ProjectPricingPanel
            projectUuid={sidePanelState.projectUuid}
            project={sidePanelState.project}
            onSaved={sidePanelState.onSaved}
            onClose={handleClose}
          />
        );
      case 'draftingCustomize':
        return (
          <DraftingCustomizePanel
            kind={sidePanelState.kind}
            draftingUuid={sidePanelState.draftingUuid}
            existingItem={sidePanelState.existingItem}
            onClose={handleClose}
          />
        );
      case 'dashboardWidgets':
        return (
          <WidgetGallery
            available={(sidePanelState.available || []) as any}
            placed={(sidePanelState.placed || []) as any}
            onAdd={sidePanelState.onAdd}
            onRemove={sidePanelState.onRemove}
            onClose={handleClose}
          />
        );
      case 'aiUsageDetail':
        /* Nothing opens this where AI usage is hidden - the surfaces that
           dispatch it are gone - but an empty slide-over is worse than none.
           See `config/featureFlags`. */
        if (!isAiUsageEnabled()) return null;
        return (
          <AIUsagePanel
            entityType={sidePanelState.entityType as AIUsageEntityType}
            entityId={sidePanelState.entityId}
            entityName={sidePanelState.entityName}
            hideHeader={false}
            onClose={handleClose}
          />
        );
      case 'scan':
        return (
          <ScanPanelContainer
            target={sidePanelState.target}
            language={sidePanelState.language}
            onClose={handleClose}
          />
        );
      case 'custom':
      default:
        return null;
    }
  };

  return (
    <>
      <div
        className={`fixed inset-0 z-[299] bg-ink/20 backdrop-blur-[2px] transition-opacity duration-300 ease-in-out ${sidePanelState.showSidePanel ? 'opacity-100 visible' : 'opacity-0 invisible'
          }`}
        onClick={handleClose}
      ></div>

      <div
        className={`fixed top-0 end-0 h-full z-[300] flex flex-col overflow-hidden glass transform transition-transform duration-300 ease-slide ${sidePanelState.showSidePanel ? 'translate-x-0' : 'translate-x-full rtl:-translate-x-full'
          }`}
        style={{ width: sidePanelState.width }}
      >
        {!sidePanelState.hideDefaultHeader && (
          <div className={`flex flex-row justify-between items-center py-4 ${sidePanelState.mb === false ? 'mb-6' : ''} border-b border-hairline-soft px-4`}>
            <div className='flex-1 min-w-0'>
              <div className='flex flex-row items-center gap-2'>
                {sidePanelState.icon && (
                  <>
                    {sidePanelState.icon}
                  </>
                )}
                {sidePanelState.message && (
                  <h3 className='text-lg leading-6 font-medium text-gray-900 dark:text-gray-100 text-start truncate min-w-0 flex-1'>{sidePanelState.message}</h3>
                )}
              </div>
              {sidePanelState.subMsg && (
                <p className='text-sm mt-4 max-w-md text-gray-500 dark:text-gray-300'>{sidePanelState.subMsg}</p>
              )}
            </div>
            <Button
              variant='outline'
              onClick={handleClose}
              size='small'
            >
              {t('Close')}
            </Button>
          </div>
        )}

        <div className="flex-1 overflow-y-auto relative flex flex-col custom-scrollbar-main" >
          {renderContent()}
        </div>
        {sidePanelState.actions && sidePanelState.actions.length > 0 && (
          <div className="border-t border-hairline-soft px-6 py-4 flex justify-end gap-3">
            {sidePanelState.actions.map((action, index) => (
              <Button
                key={index}
                variant={action.variant}
                size={action.size || 'small'}
                onClick={() => action.onClick(contentRef.current)}
              >
                {action.label}
              </Button>
            ))}
          </div>
        )}
      </div>
    </>
  );
};

export default GlobalSidePanel;
